import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Plus, Trash2, UsersRound } from "lucide-react";
import toast from "react-hot-toast";
import { useDirectoryState } from "@/components/Directory/DirectoryContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";

type ProfileGroupPolicy = {
  view: boolean;
  create: boolean;
  edit: boolean;
  delete: boolean;
};

type AccountGroup = {
  id: string;
  name: string;
  description?: string | null;
  accountDomain: string;
};

type UserRow = {
  id: string;
  email: string;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  groupIds?: string[];
};

function displayName(u: UserRow): string {
  return (
    u.name?.trim() ||
    [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
    u.email
  );
}

export function GroupsPage() {
  const state = useDirectoryState();
  const ctx = state.status === "ready" ? state.value : null;
  const domain = ctx?.org?.domain || null;

  const [policy, setPolicy] = useState<ProfileGroupPolicy | null>(null);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [tenantUsers, setTenantUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AccountGroup | null>(null);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [memberUserIds, setMemberUserIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadPolicy = useCallback(async () => {
    if (!domain || !ctx?.canManageUsers) {
      setPolicy(null);
      setPolicyLoading(false);
      return;
    }
    setPolicyLoading(true);
    try {
      const params = new URLSearchParams();
      if (ctx.isSuperAdmin) params.set("accountDomain", domain);
      const r = await fetch(
        apiUrl(`/api/security-profiles/effective-profile-groups-policy?${params.toString()}`),
        { cache: "no-store", ...apiFetchInit() },
      );
      const data = (await r.json()) as { policy?: ProfileGroupPolicy | null; error?: string };
      if (!r.ok) {
        toast.error(data.error || "Could not load group policy.");
        setPolicy(null);
        return;
      }
      setPolicy(data.policy || null);
    } catch {
      setPolicy(null);
    } finally {
      setPolicyLoading(false);
    }
  }, [ctx?.canManageUsers, ctx?.isSuperAdmin, domain]);

  const loadData = useCallback(async () => {
    if (!domain || !policy?.view) {
      setGroups([]);
      setTenantUsers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [gr, ur] = await Promise.all([
        fetch(apiUrl(`/api/groups?accountDomain=${encodeURIComponent(domain)}`), {
          cache: "no-store",
          ...apiFetchInit(),
        }),
        fetch(apiUrl(`/api/users?accountDomain=${encodeURIComponent(domain)}&limit=500`), {
          cache: "no-store",
          ...apiFetchInit(),
        }),
      ]);
      if (!gr.ok) {
        const err = (await gr.json()) as { error?: string };
        toast.error(err.error || "Could not load groups");
        setGroups([]);
      } else {
        const data = (await gr.json()) as { groups?: AccountGroup[] };
        setGroups(data.groups || []);
      }
      if (ur.ok) {
        const data = (await ur.json()) as { users?: UserRow[] };
        setTenantUsers(data.users || []);
      } else {
        setTenantUsers([]);
      }
    } catch {
      toast.error("Could not load groups");
      setGroups([]);
      setTenantUsers([]);
    } finally {
      setLoading(false);
    }
  }, [domain, policy?.view]);

  useEffect(() => {
    void loadPolicy();
  }, [loadPolicy]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const membersByGroupId = useMemo(() => {
    const map = new Map<string, UserRow[]>();
    for (const g of groups) map.set(g.id, []);
    for (const u of tenantUsers) {
      for (const gid of u.groupIds || []) {
        const list = map.get(gid);
        if (list) list.push(u);
      }
    }
    return map;
  }, [groups, tenantUsers]);

  const openCreate = () => {
    if (!policy?.create) return;
    setEditing(null);
    setFormName("");
    setFormDescription("");
    setMemberUserIds([]);
    setDialogOpen(true);
  };

  const openEdit = (g: AccountGroup) => {
    if (!policy?.edit) return;
    setEditing(g);
    setFormName(g.name);
    setFormDescription(g.description || "");
    const members = membersByGroupId.get(g.id) || [];
    setMemberUserIds(members.map((m) => m.id));
    setDialogOpen(true);
  };

  const syncGroupMembers = async (groupId: string) => {
    if (!policy?.edit) return true;
    const selected = new Set(memberUserIds);
    const updates: { id: string; groupIds: string[] }[] = [];

    for (const u of tenantUsers) {
      const current = u.groupIds || [];
      const has = current.includes(groupId);
      const shouldHave = selected.has(u.id);
      if (has === shouldHave) continue;
      const next = shouldHave
        ? [...new Set([...current, groupId])]
        : current.filter((gid) => gid !== groupId);
      updates.push({ id: u.id, groupIds: next });
    }

    if (updates.length === 0) return true;

    const results = await Promise.all(
      updates.map(({ id, groupIds }) =>
        fetch(apiUrl(`/api/users/${encodeURIComponent(id)}`), {
          method: "PUT",
          ...apiFetchInit(),
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groupIds }),
        }),
      ),
    );

    const failed = results.filter((r) => !r.ok);
    if (failed.length > 0) {
      toast.error(
        `Group saved, but ${failed.length} membership update(s) failed. Check permissions and try again.`,
      );
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!policy?.edit && editing) {
      toast.error("Editing profile groups is disabled for this account.");
      return;
    }
    if (!policy?.create && !editing) {
      toast.error("Creating profile groups is disabled for this account.");
      return;
    }
    const name = formName.trim();
    if (!name) {
      toast.error("Name is required");
      return;
    }
    if (!domain) {
      toast.error("Organization domain is missing");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const r = await fetch(apiUrl(`/api/groups/${encodeURIComponent(editing.id)}`), {
          method: "PUT",
          ...apiFetchInit(),
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            description: formDescription.trim() || null,
          }),
        });
        if (!r.ok) {
          const err = (await r.json()) as { error?: string };
          toast.error(err.error || "Could not update group");
          return;
        }
        const membersOk = await syncGroupMembers(editing.id);
        if (membersOk) {
          toast.success("Group and members updated");
          setDialogOpen(false);
        }
        await loadData();
        return;
      }

      const createRes = await fetch(apiUrl("/api/groups"), {
        method: "POST",
        ...apiFetchInit(),
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          description: formDescription.trim() || null,
          accountDomain: domain,
        }),
      });
      if (!createRes.ok) {
        const err = (await createRes.json()) as { error?: string };
        toast.error(err.error || "Could not create group");
        return;
      }
      toast.success("Group created");
      setDialogOpen(false);
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (g: AccountGroup) => {
    if (!policy?.delete) {
      toast.error("Deleting profile groups is disabled for this account.");
      return;
    }
    if (
      !window.confirm(
        `Delete group "${g.name}"? Users will be removed from this group; accounts are not deleted.`,
      )
    ) {
      return;
    }
    setDeletingId(g.id);
    try {
      const r = await fetch(apiUrl(`/api/groups/${encodeURIComponent(g.id)}`), {
        method: "DELETE",
        ...apiFetchInit(),
      });
      if (!r.ok) {
        const err = (await r.json()) as { error?: string };
        toast.error(err.error || "Could not delete group");
        return;
      }
      toast.success("Group deleted");
      await loadData();
    } finally {
      setDeletingId(null);
    }
  };

  if (!ctx) return null;

  if (!ctx.canManageUsers) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Groups</h1>
        <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-100">
          Groups live here. Your role in this organization is not Organization Owner or Account
          Admin, so the catalog stays locked. Check the account menu for the active org.
        </p>
      </div>
    );
  }

  if (policyLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-8 animate-spin text-gray-400" aria-label="Loading" />
      </div>
    );
  }

  if (policy && !policy.view) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Groups</h1>
        <p className="rounded-md border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 dark:border-white/10 dark:bg-[#1D232D] dark:text-gray-300">
          Viewing the group catalog is turned off for this account. Enable profile group access under
          organization settings on ATW One.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">All groups</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Profile groups for {ctx.org?.companyName || domain}. Create groups, edit membership, and
            use them for access across ATW.
          </p>
        </div>
        <Button onClick={openCreate} disabled={!domain || !policy?.create}>
          <Plus className="mr-2 size-4" aria-hidden />
          New group
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-[#1D232D]">
        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-white/10">
          <UsersRound className="size-5 text-gray-500" aria-hidden />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Group catalog</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {domain ? `Showing groups for ${domain}` : "No organization domain"}
            </p>
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading" />
          </div>
        ) : groups.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-500">No groups yet. Create one to assign people.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-gray-500 dark:border-white/10 dark:text-gray-400">
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Description</th>
                  <th className="px-4 py-2 font-medium">Members</th>
                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                {groups.map((g) => {
                  const members = membersByGroupId.get(g.id) || [];
                  return (
                    <tr key={g.id}>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{g.name}</td>
                      <td className="max-w-xs px-4 py-3 text-gray-600 dark:text-gray-400">
                        {g.description || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {members.length === 0
                          ? "None"
                          : members
                              .slice(0, 3)
                              .map((m) => displayName(m))
                              .join(", ") + (members.length > 3 ? ` +${members.length - 3}` : "")}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!policy?.edit}
                            onClick={() => openEdit(g)}
                          >
                            <Pencil className="size-3.5" aria-hidden />
                            Edit
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!policy?.delete || deletingId === g.id}
                            onClick={() => void handleDelete(g)}
                          >
                            {deletingId === g.id ? (
                              <Loader2 className="size-3.5 animate-spin" aria-hidden />
                            ) : (
                              <Trash2 className="size-3.5" aria-hidden />
                            )}
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit group" : "New group"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update the group name, description, and member assignments."
                : "Create a profile group for your organization."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="group-name" className="block border-0 bg-transparent p-0 dark:bg-transparent">
                Name
              </Label>
              <Input id="group-name" value={formName} onChange={(e) => setFormName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="group-desc" className="block border-0 bg-transparent p-0 dark:bg-transparent">
                Description
              </Label>
              <Input
                id="group-desc"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
              />
            </div>
            {editing && policy?.edit ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Members</p>
                <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border border-gray-200 p-3 dark:border-white/10">
                  {[...tenantUsers]
                    .sort((a, b) => displayName(a).localeCompare(displayName(b)))
                    .map((u) => (
                      <label key={u.id} className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={memberUserIds.includes(u.id)}
                          onChange={(e) => {
                            setMemberUserIds((prev) =>
                              e.target.checked ? [...prev, u.id] : prev.filter((id) => id !== u.id),
                            );
                          }}
                        />
                        <span>
                          {displayName(u)}{" "}
                          <span className="text-gray-500 dark:text-gray-400">({u.email})</span>
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="button" disabled={saving} onClick={() => void handleSave()}>
                {saving ? "Saving…" : editing ? "Save changes" : "Create group"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
